import { coreCustomersCurrentFeatureFlagsGetAsyncSlice } from '@slice/customers/current/feature-flags/get';
import { CoreDataDictionaryViewsSharedKeyName } from './name';
import { coreStakeholdersGetAsyncSlice } from '@slice/stakeholders/get';
import { coreTagsGetAsyncSlice } from '@slice/tags/get';
import { coreUsersAllGetAsyncSlice } from '@slice/users/all/get';
import { coreUsersCurrentGetAsyncSlice } from '@slice/users/current/get';
import { createGetAsyncSlice } from '@sonar-software/xuder';
import { getSharedDataDictionary } from '@api';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { updateApplicationReferences } from '../../../data-security/applications/_idMap/actions';
import { updateSonarObjectReferences } from '@slice/sonar-objects/_idMap/actions';
import { updateStakeholderReferences } from '@slice/stakeholders/_idMap/actions';
import { updateTagReferences } from '@slice/tags/_idMap/actions';
import { updateUserReferences } from '@slice/users/_idMap/actions';

import { buildUpdateForField, buildUpdateForObject } from '../../get';
import {
	DataDictionaryField,
	DataDictionaryObject,
	FeatureFlagDictionary,
	UpdateSonarObjectReferenceRequest,
	User,
	UserWithMetadata
} from '@types';

const { receiveGetCoreCustomersCurrentFeatureFlags } =
	coreCustomersCurrentFeatureFlagsGetAsyncSlice.asyncActionCreators;

const { receiveGetCoreUsersAll } = coreUsersAllGetAsyncSlice.asyncActionCreators;
const { receiveGetCoreUsersCurrent } = coreUsersCurrentGetAsyncSlice.asyncActionCreators;
const { receiveGetCoreStakeholders } = coreStakeholdersGetAsyncSlice.asyncActionCreators;
const { receiveGetCoreTags } = coreTagsGetAsyncSlice.asyncActionCreators;

export const getCoreDataDictionaryViewsSharedKeyAsyncSlice = createGetAsyncSlice({
	httpSagaOptions: {
		preRequestActionCreators: [
			() =>
				receiveGetCoreUsersCurrent({
					isGuest: true,
					isReadOnly: true
				} as User),
			() => receiveGetCoreCustomersCurrentFeatureFlags({} as FeatureFlagDictionary)
		],
		successDataActionCreators: [
			(response) =>
				receiveGetCoreUsersAll({
					results: response.owners.map((owner) => {
						const [ firstName, lastName ] = owner.name.split(' ');

						return {
							firstName,
							fullName: owner.name,
							id: owner.id,
							lastName
						} as UserWithMetadata;
					})
				}),
			(response) =>
				updateUserReferences(
					response.owners.map((owner) => {
						const [ firstName, lastName ] = owner.name.split(' ');

						return {
							firstName,
							fullName: owner.name,
							lastName,
							userId: owner.id
						};
					})
				),
			(response) =>
				receiveGetCoreStakeholders(
					response.stakeholders.map((stakeholder) => ({
						name: stakeholder.name,
						stakeholderId: stakeholder.id,
						userId: stakeholder.userId
					}))
				),
			(response) =>
				updateStakeholderReferences(
					response.stakeholders.map((stakeholder) => ({
						name: stakeholder.name,
						stakeholderId: stakeholder.id,
						userId: stakeholder.userId
					}))
				),
			(response) =>
				updateUserReferences(
					response.stakeholders
						.filter((stakeholder) => Boolean(stakeholder.userId))
						.map((stakeholder) => {
							const [ firstName, lastName ] = stakeholder.name.split(' ');

							return {
								firstName,
								fullName: stakeholder.name,
								lastName,
								userId: stakeholder.userId
							};
						})
				),
			(response) =>
				receiveGetCoreTags(
					response.tags.map((tag) => ({
						autoTagged: false,
						description: '',
						hexColor: tag.color,
						id: tag.id,
						name: tag.name,
						ownerIds: []
					}))
				),
			(response) =>
				updateTagReferences(
					response.tags.map((tag) => ({
						autoTagged: false,
						description: '',
						hexColor: tag.color,
						id: tag.id,
						name: tag.name,
						ownerIds: []
					}))
				),
			(response) =>
				updateApplicationReferences(response.applications),
			(response) => {
				const context: Array<UpdateSonarObjectReferenceRequest> = response.results.map((result) =>
					((result as DataDictionaryObject).isCustom === undefined ?
						buildUpdateForField(result as DataDictionaryField) :
						buildUpdateForObject(result as DataDictionaryObject, SonarObjectType.SalesforceEntity)));

				return updateSonarObjectReferences(context);
			}
		]
	},
	name: CoreDataDictionaryViewsSharedKeyName,
	receive: true,
	request: true,
	requestHandler: getSharedDataDictionary,
	retainDataOnRequest: true
});
